import { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { AUTO_OPEN_MENUS } from '../../config/menuConfig'
import { MenuItem } from '../../types/layout'

/**
 * Hook para manejar el estado de apertura/cierre de los menús del sidebar
 * Auto-abre menús según la ruta actual
 */
export const useMenuState = (menuItems: MenuItem[]) => {
  const location = useLocation()
  
  // Crear estado inicial basado en los IDs de los menús
  const initialMenuState = useMemo(() => {
    const state: Record<string, boolean> = {}
    menuItems.forEach(item => {
      // Auto-abrir si la ruta actual coincide con alguna configuración
      const shouldAutoOpen = AUTO_OPEN_MENUS[location.pathname]?.includes(item.id) || 
                            location.pathname.startsWith(item.path)
      state[item.id] = shouldAutoOpen || false
    })
    return state
  }, [menuItems, location.pathname])

  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(initialMenuState)

  // Actualizar estado cuando cambia la ruta
  useEffect(() => {
    setOpenMenus(prev => {
      const newState: Record<string, boolean> = {}
      
      menuItems.forEach(item => {
        // Verificar si la ruta actual requiere abrir este menú
        const shouldAutoOpen = AUTO_OPEN_MENUS[location.pathname]?.includes(item.id) ||
                              location.pathname.startsWith(item.path) ||
                              (item.submenu?.some(sub => location.pathname.startsWith(sub.path)))
        
        // Mantener el estado actual si no debe auto-abrirse
        newState[item.id] = shouldAutoOpen || prev[item.id] || false
      })
      
      return { ...prev, ...newState }
    })
  }, [location.pathname, menuItems])

  const toggleMenu = (menuId: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId],
    }))
  }

  const openMenu = (menuId: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuId]: true,
    }))
  }

  const closeMenu = (menuId: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuId]: false,
    }))
  }

  const isMenuOpen = (menuId: string) => openMenus[menuId] || false

  return {
    openMenus,
    toggleMenu,
    openMenu,
    closeMenu,
    isMenuOpen,
  }
}

